import React, { useState, useRef, useCallback } from 'react';
import { callApi, addParamTolookUpQuery, getPropValue } from './utils';
import './AutoComplete.css';
import useApp from '../AppContext';
import { t } from "./../i18n";

const AutoComplete = ({
  placeholderPrimary,
  placeholderSecondary,
  serverUrl,
  props,
  col,
  obj,
  disabled,
  onChange,
}) => {
  const app = useApp()
  const initialPrimaryQry = getPropValue(obj, col.columnName) || '';
  const initialSecondaryQry = getPropValue(obj, col.secondaryField) || '';
  const [language] = useState(app.sessionToken?.Language);
  const [query, setQuery] = useState({ primary: initialPrimaryQry, secondary: initialSecondaryQry });
  const [options, setOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const fetchTimeout = useRef(null);
  const primaryInputRef = useRef(null);
  const secondaryInputRef = useRef(null);
  const containerRef = useRef(null);

  const fetchOptions = useCallback(async (primaryQuery, secondaryQuery) => {
    try {
      const lkpQuery = addParamTolookUpQuery(col.lookUpQuery);
      const variables = {
        filterName: 'lookupQry',
        top: 50,
        [lkpQuery.lookUpField]: `${primaryQuery}%`,
        ...(col.secondaryField ? { [lkpQuery.secondaryField]: `${secondaryQuery}%` } : {}),
      };
      const gqlResult = await callApi('POST', `${serverUrl}/api/query`, props.userToken, {
        query: lkpQuery.queryStr,
        variables: variables
      });

      const fetchedData = gqlResult.data[Object.keys(gqlResult.data)[0]] || [];
      const newOptions = fetchedData.slice(0, 50).map(item => ({
        pkField: item[lkpQuery.pkField] || '',
        lkpField: item[lkpQuery.lookUpField] || '',
        secondaryField: item[lkpQuery.secondaryField] || '',
      }));

      setOptions(newOptions);
    } catch (error) {
      setOptions([]);
    }
  }, [col.lookUpQuery, props.userToken, serverUrl, col.secondaryField]);

  const delayedFetchOptions = useCallback((primaryQuery, secondaryQuery) => {
    clearTimeout(fetchTimeout.current);
    fetchTimeout.current = setTimeout(() => {
      fetchOptions(primaryQuery, secondaryQuery);
    }, 750);
  }, [fetchOptions]);

  const handleInputChange = useCallback((event, isPrimary) => {
    const value = event.target.value;
    setOptions([]);
    if (isPrimary) {
      setQuery({
        primary: value,
        secondary: '',
      });
      delayedFetchOptions(value, '');
    } else {
      setQuery({
        primary: '',
        secondary: value,
      });
      delayedFetchOptions('', value);
    }

    if (!value && onChange) {
      onChange(null);
    }
    setIsOpen(true); // Open dropdown on input change
  }, [delayedFetchOptions, onChange]);

  const handleOptionClick = useCallback((option) => {
    setQuery({ primary: option.lkpField, secondary: option.secondaryField });
    if (onChange) {
      onChange({ option });
    }
    setIsOpen(false);
  }, [onChange]);

  const handleFocus = useCallback(() => {
    if (query.primary === '' && query.secondary === '') {
      setIsOpen(true);
      delayedFetchOptions('', '');
    }
  }, [query.primary, query.secondary, delayedFetchOptions]);

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (
        document.activeElement !== primaryInputRef.current &&
        document.activeElement !== secondaryInputRef.current &&
        !containerRef.current.contains(document.activeElement)
      ) {
        setIsOpen(false);
      }
    }, 0);
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (!isOpen) return;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : options.length - 1));
        break;
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex(prevIndex => (prevIndex < options.length - 1 ? prevIndex + 1 : 0));
        break;
      case 'Enter':
        if (activeIndex >= 0) {
          handleOptionClick(options[activeIndex]);
        }
        break;
      default:
        break;
    }
  }, [isOpen, options, activeIndex, handleOptionClick]);

  return (
    <div ref={containerRef} className="AutoComplete-container" onKeyDown={handleKeyDown}>
      <div className="AutoComplete-input-container">
        <label className="AutoComplete-label">{col.caption}</label>
        <input
          type="text"
          placeholder={placeholderPrimary}
          value={query.primary}
          onChange={event => handleInputChange(event, true)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          className="form-control"
          style={{ width: '30%' }}
          size="sm"
          ref={primaryInputRef}
        />
        <input
          type="text"
          placeholder={placeholderSecondary}
          value={query.secondary}
          onChange={event => handleInputChange(event, false)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          className="form-control ms-2"
          style={{ width: '70%' }}
          size="sm"
          ref={secondaryInputRef}
        />
      </div>
      {isOpen && (
        <ul className="AutoComplete-dropdown">
          <span>{t('common.choose', language)} {col.caption}
          </span>
          <div className='AutoComplete-wrapper'>
            {options.map((option, index) => (
              <li
                key={option.pkField}
                className={`AutoComplete-option ${index === activeIndex ? 'active' : ''}`}
                onMouseDown={() => handleOptionClick(option)}
              >
                <div>{option.lkpField}</div>
                <div>{option.secondaryField}</div>
              </li>
            ))}
          </div>
          {options.length === 0 && (
            <p
              className="d-block mx-auto"
              style={{ width: 'fit-content', marginTop: '40px', color: 'rgb(85 82 82)', fontSize: '13px' }}
            >{t('common.noData', language)}</p>
          )}
        </ul>
      )}
    </div>
  );
};

export default AutoComplete;
